import { connectorSurface } from './connector_surface.js';
export const ACTION_VERB = {
    pause: 'pause',
    resume: 'resume',
    delete: 'delete',
    deploy: 'publish',
    auth_change: 'change the authentication method of',
};
/* ------------------------------------------------------------ derivations */
/** Error rate is the complement of success — never stored twice. */
export function connectorErrorRate(c) {
    return Math.round((100 - c.successRate) * 10) / 10;
}
/** A connector counts as active when it is deployed and serving. */
export function isActiveConnector(c) {
    return c.status === 'deployed' && c.live;
}
/** Health counts for the Overview donut. Always returns all four buckets. */
export function healthBreakdown(connectors) {
    const out = { healthy: 0, warning: 0, critical: 0, unknown: 0 };
    for (const c of connectors)
        out[c.health] += 1;
    return out;
}
/** Fleet rollup for the Overview KPI row. Empty-safe: zero connectors → zeroes. */
export function fleetMetrics(connectors) {
    const active = connectors.filter(isActiveConnector);
    const executions = connectors.reduce((n, c) => n + c.totalExecutions, 0);
    // Weight success by execution volume — a connector with 4 calls must not sway
    // the fleet number as much as one with 40,000.
    const weighted = connectors.reduce((n, c) => n + c.successRate * c.totalExecutions, 0);
    const successRate = executions ? Math.round((weighted / executions) * 10) / 10 : 0;
    const withLatency = connectors.filter((c) => c.avgLatencyMs != null);
    const avgLatencyMs = withLatency.length
        ? Math.round(withLatency.reduce((n, c) => n + (c.avgLatencyMs ?? 0), 0) / withLatency.length)
        : null;
    return {
        total: connectors.length,
        active: active.length,
        executions,
        successRate,
        errorRate: executions ? Math.round((100 - successRate) * 10) / 10 : 0,
        avgLatencyMs,
        driftCount: connectors.filter((c) => c.drift.detected).length,
    };
}
/**
 * What an approver is signing off on. Computed from the connector's own state so
 * the request and the banner always describe the same consequences.
 */
export function actionImplications(c, type, triggerCount) {
    switch (type) {
        case 'pause':
            return [
                `Every agent currently calling ${c.name} will start failing immediately.`,
                `${triggerCount} external trigger(s) on this connector stop firing until resumed.`,
                'Stored credentials and version history are not affected.',
            ];
        case 'resume':
            return [
                `${c.name} starts serving traffic again for every agent granted access.`,
                `${triggerCount} external trigger(s) resume firing.`,
            ];
        case 'delete': {
            const surface = connectorSurface(c);
            return [
                'This cannot be undone.',
                `All version history and schema mappings for ${c.name} are permanently lost.`,
                /* Two different relationships, and one verb cannot carry both. A
                   trigger is somebody else's thing pointed at this connector, and it
                   breaks because what it points at is gone. What the connector exposes
                   is not pointed at anything — it is part of what is being deleted.
                   And it is tools on an MCP server, which is not this sentence's call
                   to make (`connector_surface.ts`). */
                `${triggerCount} trigger(s) referencing this connector will break immediately, along with its own ${surface.count} ${surface.unit}(s).`,
            ];
        }
        case 'deploy':
            return c.pendingDeploy
                ? [
                    'This version becomes the live one immediately for all callers.',
                    'No breaking changes detected in this version.',
                ]
                : [
                    `v${c.version} becomes the live version immediately for all callers.`,
                    'No pending version: this republishes the current version with no code change.',
                ];
        case 'auth_change':
            return [
                `Every end user currently authenticating against ${c.name} will need to re-authenticate with the new method — their existing credentials become invalid the moment this changes.`,
                `${triggerCount} external trigger(s) using the current auth method may also need reconfiguring.`,
                'This does not affect version history or stored connector configuration beyond the auth method itself.',
            ];
    }
}
/**
 * Connector instances grouped by the app they belong to — the Connectors catalog
 * is app-keyed so its list is exactly the org's app list (ADR-0014), with
 * multi-instance apps expanding rather than appearing twice.
 */
export function groupByApp(connectors) {
    const out = new Map();
    for (const c of connectors) {
        const list = out.get(c.appKey);
        if (list)
            list.push(c);
        else
            out.set(c.appKey, [c]);
    }
    return out;
}
/** The version marked current, else the newest entry, else undefined. */
export function currentVersion(c) {
    return c.versions.find((v) => v.version === c.version) ?? c.versions[0];
}
/**
 * Access control, as three separate questions.
 *
 * They were one, and conflating them is what made Shared meaningless: a
 * connector nobody could *use* was also a connector nobody could *find*, so
 * there was no way to build the case for asking. The three levels only differ
 * once discovery and use are asked apart.
 *
 * | level   | who finds it in the catalog | who can connect an agent |
 * |---------|-----------------------------|--------------------------|
 * | private | owner + admins              | owner + admins           |
 * | shared  | everyone in the org         | owner + admins + the list|
 * | org     | everyone in the org         | everyone in the org      |
 *
 * `viewer` is a display name because that is what a connector records as its
 * `owner` and carries in `sharedWith` — the data names people the way a person
 * would. Comparison is case-insensitive for the same reason.
 */
/** Everyone who may edit it whatever its level: its creator, and any admin. */
export function canEditConnector(c, viewer, isAdmin = false) {
    if (isAdmin)
        return true;
    // No owner recorded is authored data that predates ownership; nothing to enforce.
    if (!c.owner)
        return true;
    if (viewer.toLowerCase() === c.owner.toLowerCase())
        return true;
    // An Editor named on its list — on a Private (shared) connector or a Public
    // (org) one. Without this the role would be a label that grants nothing,
    // which is worse than not offering it.
    return (c.access === 'shared' || c.access === 'org') && sharedRoleOf(c, viewer) === 'editor';
}
/**
 * The role this person holds on this connector, or undefined if they hold
 * none. The owner answers `'owner'` whatever the shared list says — ownership
 * outranks a nomination, and a connector shared back to its own owner should
 * not demote them.
 */
export function sharedRoleOf(c, viewer) {
    const who = viewer.toLowerCase();
    if (c.owner && c.owner.toLowerCase() === who)
        return 'owner';
    const named = (c.sharedWith ?? []).find((n) => n.toLowerCase() === who);
    if (!named)
        return undefined;
    return c.sharedRoles?.[named] ?? 'operator';
}
/**
 * Whether this connector appears in the catalog at all.
 *
 * Only Private hides. A Shared connector is deliberately discoverable by the
 * whole org — seeing that it exists is what lets somebody ask for it, and a
 * catalog that hid everything you could not already use would make the hub
 * look emptier the more carefully it was governed.
 */
export function canDiscoverConnector(c, viewer, isAdmin = false) {
    if (c.access !== 'private')
        return true;
    return canEditConnector(c, viewer, isAdmin);
}
/**
 * Whether this viewer may connect an agent to it — the question the buttons
 * on the page are gated on.
 *
 * Sharing is with people, never with agents. A person connects the agents they
 * run to the connectors they hold, so an agent's reach is its owner's reach and
 * there is no second list to keep in step.
 */
export function canUseConnector(c, viewer, isAdmin = false) {
    if (c.access === 'org')
        return true;
    if (canEditConnector(c, viewer, isAdmin))
        return true;
    if (c.access === 'shared') {
        return (c.sharedWith ?? []).some((n) => n.toLowerCase() === viewer.toLowerCase());
    }
    return false;
}
//# sourceMappingURL=connectors.js.map